import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Target, ArrowRight } from "lucide-react";
import { Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import type { Goal } from "@shared/schema";
import { isGoalVisibleOnMainSurfaces } from "@/lib/goal-visibility";

interface ActiveGoalsWidgetProps {
  maxItems?: number;
}

export function ActiveGoalsWidget({ maxItems = 5 }: ActiveGoalsWidgetProps) {
  const { data: goals = [], isLoading } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const activeGoals = goals
    .filter(isGoalVisibleOnMainSurfaces)
    .filter(goal => goal.status === "active")
    .slice(0, maxItems);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-3 rounded-lg border border-border">
            <Skeleton className="h-4 w-3/4 mb-2" />
            <Skeleton className="h-2 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (activeGoals.length === 0) {
    return (
      <div className="text-center py-8">
        <Target className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground mb-4">
          No active goals yet. Start creating goals to track your progress!
        </p>
        <Link href="/goals">
          <Button size="sm" data-testid="button-create-goal">
            <Target className="h-4 w-4 mr-2" />
            Create Goal
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {activeGoals.map((goal) => (
        <Link key={goal.id} href={`/goals/${goal.id}`}>
          <div
            className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
            data-testid={`goal-item-${goal.id}`}
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-medium text-sm text-foreground line-clamp-1">
                {goal.title}
              </h4>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded ml-2 flex-shrink-0">
                {goal.category}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Progress value={goal.progress} className="h-1.5 flex-1" />
              <span className="text-xs text-muted-foreground font-medium min-w-[3ch]">
                {goal.progress}%
              </span>
            </div>
          </div>
        </Link>
      ))}
      
      {goals.filter(isGoalVisibleOnMainSurfaces).filter(g => g.status === "active").length > maxItems && (
        <Link href="/goals">
          <Button
            variant="ghost"
            className="w-full"
            size="sm"
            data-testid="button-view-all-goals"
          >
            View All Goals
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Link>
      )}
    </div>
  );
}
